import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { webhookAPI, authAPI } from '../services/api';
import { removeToken } from '../utils/auth';
import './Webhooks.css';

function Webhooks() {
    const [webhooks, setWebhooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingWebhook, setEditingWebhook] = useState(null);
    const [userName, setUserName] = useState('');
    const [userRole, setUserRole] = useState(null);
    const [executionLogs, setExecutionLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        webhookUrl: '',
        expectedResponse: '',
        webhookDataFormat: 'signD',
        queueExpirationMinutes: 60,
        onlySubmitPositiveResults: false,
        customHeaders: []
    });

    useEffect(() => {
        loadUserInfo();
        loadWebhooks();
        loadExecutionLogs();
    }, []);

    const loadUserInfo = async () => {
        try {
            const response = await authAPI.getCurrentUser();
            const user = response.data.data.user;
            setUserRole(user.role);
            setUserName(user.name);
        } catch (err) {
            console.error('Failed to load user info:', err);
        }
    };

    const loadWebhooks = async () => {
        try {
            setLoading(true);
            const response = await webhookAPI.getWebhooks();
            setWebhooks(response.data.data.webhooks || []);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load webhooks');
        } finally {
            setLoading(false);
        }
    };

    const loadExecutionLogs = async () => {
        try {
            setLogsLoading(true);
            const response = await webhookAPI.getRecentExecutionLogs();
            setExecutionLogs(response.data.data.logs || []);
        } catch (err) {
            console.error('Failed to load execution logs:', err);
        } finally {
            setLogsLoading(false);
        }
    };

    const handleLogout = () => {
        removeToken();
        navigate('/login');
    };

    const openCreateModal = () => {
        setEditingWebhook(null);
        setFormData({
            name: '',
            webhookUrl: '',
            expectedResponse: '',
            webhookDataFormat: 'signD',
            queueExpirationMinutes: 60,
            onlySubmitPositiveResults: false,
            customHeaders: []
        });
        setShowModal(true);
        setError('');
        setSuccess('');
    };

    const openEditModal = (webhook) => {
        setEditingWebhook(webhook);

        // Ensure customHeaders is always an array
        let customHeaders = [];
        if (webhook.customHeaders) {
            if (Array.isArray(webhook.customHeaders)) {
                customHeaders = webhook.customHeaders;
            } else if (typeof webhook.customHeaders === 'string') {
                try {
                    const parsed = JSON.parse(webhook.customHeaders);
                    customHeaders = Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                    console.error('Failed to parse customHeaders:', e);
                    customHeaders = [];
                }
            } else if (typeof webhook.customHeaders === 'object') {
                // Convert object to array of key-value pairs
                customHeaders = Object.entries(webhook.customHeaders).map(([key, value]) => ({
                    key,
                    value
                }));
            }
        }

        setFormData({
            name: webhook.name,
            webhookUrl: webhook.webhookUrl,
            expectedResponse: webhook.expectedResponse || '',
            webhookDataFormat: webhook.webhookDataFormat,
            queueExpirationMinutes: webhook.queueExpirationMinutes,
            onlySubmitPositiveResults: webhook.onlySubmitPositiveResults || false,
            customHeaders: customHeaders
        });
        setShowModal(true);
        setError('');
        setSuccess('');
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingWebhook(null);
        setError('');
        setSuccess('');
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const addCustomHeader = () => {
        setFormData(prev => ({
            ...prev,
            customHeaders: [...prev.customHeaders, { key: '', value: '' }]
        }));
    };

    const removeCustomHeader = (index) => {
        setFormData(prev => ({
            ...prev,
            customHeaders: prev.customHeaders.filter((_, i) => i !== index)
        }));
    };

    const updateCustomHeader = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            customHeaders: prev.customHeaders.map((header, i) =>
                i === index ? { ...header, [field]: value } : header
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validation
        if (!formData.name.trim()) {
            setError('Name is required');
            return;
        }

        if (!formData.webhookUrl.trim()) {
            setError('Webhook URL is required');
            return;
        }

        try {
            const url = new URL(formData.webhookUrl);
            if (!url.protocol.startsWith('http')) {
                setError('Webhook URL must use HTTP or HTTPS');
                return;
            }
        } catch (err) {
            setError('Invalid webhook URL');
            return;
        }

        if (formData.queueExpirationMinutes < 1 || formData.queueExpirationMinutes > 1440) {
            setError('Queue expiration must be between 1 and 1440 minutes');
            return;
        }

        try {
            if (editingWebhook) {
                await webhookAPI.updateWebhook(editingWebhook.id, formData);
                setSuccess('Webhook updated successfully');
            } else {
                await webhookAPI.createWebhook(formData);
                setSuccess('Webhook created successfully');
            }

            setTimeout(() => {
                closeModal();
                loadWebhooks();
                loadExecutionLogs();
            }, 1000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save webhook');
        }
    };

    const handleDelete = async (webhook) => {
        if (!window.confirm(`Are you sure you want to delete webhook "${webhook.name}"?`)) {
            return;
        }

        try {
            await webhookAPI.deleteWebhook(webhook.id);
            setSuccess('Webhook deleted successfully');
            setTimeout(() => setSuccess(''), 3000);
            loadWebhooks();
            loadExecutionLogs();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete webhook');
            setTimeout(() => setError(''), 3000);
        }
    };

    return (
        <div className="webhooks-page">
            <header className="page-header">
                <div>
                    <h1>My Webhooks</h1>
                    {userName && (
                        <p className="user-info">
                            {userName} {userRole && <span className="badge">{userRole}</span>}
                        </p>
                    )}
                </div>
                <div className="header-actions">
                    <button onClick={() => navigate('/')} className="secondary-btn">
                        Back to Dashboard
                    </button>
                    <button onClick={handleLogout} className="logout-btn">
                        Logout
                    </button>
                </div>
            </header>

            <nav className="dashboard-nav">
                <button
                    onClick={() => navigate('/')}
                    className="nav-btn"
                >
                    ID Scanners
                </button>
                <button
                    onClick={() => navigate('/webhooks')}
                    className="nav-btn active"
                >
                    My Webhooks
                </button>
                <button
                    onClick={() => navigate('/security-policies')}
                    className="nav-btn"
                >
                    Security Policies
                </button>
                <button
                    onClick={() => navigate('/operational-log')}
                    className="nav-btn"
                >
                    Operational Log
                </button>
            </nav>

            <div className="page-content">
                <div className="page-header">
                    <h2>Webhooks</h2>
                    <p>Configure webhooks to receive notifications about scan results and events</p>
                </div>

                <div className="page-actions">
                    <button onClick={openCreateModal} className="primary-btn">
                        + Create Webhook
                    </button>
                </div>

                {error && !showModal && <div className="error">{error}</div>}
                {success && !showModal && <div className="success">{success}</div>}

                {loading ? (
                    <div className="loading">Loading webhooks...</div>
                ) : webhooks.length === 0 ? (
                    <div className="empty-state">
                        <p>No webhooks found. Create your first webhook to get started.</p>
                    </div>
                ) : (
                    <div className="webhooks-grid">
                        {webhooks.map((webhook) => (
                            <div key={webhook.id} className="webhook-card">
                                <div className="webhook-card-header">
                                    <h3>{webhook.name}</h3>
                                    <span className="format-badge">{webhook.webhookDataFormat || webhook.dataFormat || webhook.data_format || 'N/A'}</span>
                                </div>

                                <div className="webhook-card-body">
                                    <div className="info-row">
                                        <span className="label">Webhook URL:</span>
                                        <span className="value truncate" title={webhook.webhookUrl}>
                                            {webhook.webhookUrl}
                                        </span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Queue Expiration:</span>
                                        <span className="value">{webhook.queueExpirationMinutes} minutes</span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Only Positive Results:</span>
                                        <span className="value">{webhook.onlySubmitPositiveResults ? 'Yes' : 'No'}</span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Custom Headers:</span>
                                        <span className="value">
                                            {webhook.customHeaders && webhook.customHeaders.length > 0
                                                ? `Yes (${webhook.customHeaders.length} header${webhook.customHeaders.length > 1 ? 's' : ''})`
                                                : 'No'}
                                        </span>
                                    </div>

                                    {webhook.usageCount !== undefined && (
                                        <div className="info-row">
                                            <span className="label">Used by:</span>
                                            <span className="value">{webhook.usageCount} scanner(s)</span>
                                        </div>
                                    )}
                                </div>

                                <div className="webhook-card-footer">
                                    <button
                                        onClick={() => openEditModal(webhook)}
                                        className="edit-btn"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(webhook)}
                                        className="delete-btn"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="execution-logs-section">
                    <h2>Recent Webhook Execution Logs</h2>
                    <p className="section-description">
                        All outgoing webhook attempts across all your webhooks, including retries and their results
                    </p>

                    {logsLoading ? (
                        <div className="loading">Loading execution logs...</div>
                    ) : executionLogs.length === 0 ? (
                        <div className="empty-state">
                            <p>No webhook execution logs yet. Logs will appear here when webhooks are triggered.</p>
                        </div>
                    ) : (
                        <div className="logs-table-container">
                            <table className="logs-table">
                                <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>Webhook</th>
                                        <th>Scanner</th>
                                        <th>Attempt</th>
                                        <th>Status</th>
                                        <th>HTTP Code</th>
                                        <th>Duration</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {executionLogs.map((log) => (
                                        <tr key={log.id}>
                                            <td>{new Date(log.createdAt).toLocaleString()}</td>
                                            <td>{log.webhookName || `Webhook #${log.webhookId}`}</td>
                                            <td>{log.scannerName || `Scanner #${log.scannerId}`}</td>
                                            <td>
                                                <span className={`attempt-badge ${log.attemptNumber > 1 ? 'retry' : 'first'}`}>
                                                    {log.attemptNumber === 1 ? '1st attempt' : `Retry #${log.attemptNumber}`}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge status-${log.status.toLowerCase()}`}>
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td>{log.responseStatusCode || 'N/A'}</td>
                                            <td>{log.durationMs ? `${log.durationMs}ms` : 'N/A'}</td>
                                            <td className="error-message">
                                                {log.errorMessage || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingWebhook ? 'Edit Webhook' : 'Create Webhook'}</h2>
                            <button className="close-btn" onClick={closeModal}>×</button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {error && <div className="error">{error}</div>}
                            {success && <div className="success">{success}</div>}

                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="My Webhook"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Webhook URL *</label>
                                <input
                                    type="url"
                                    value={formData.webhookUrl}
                                    onChange={(e) => handleInputChange('webhookUrl', e.target.value)}
                                    placeholder="https://example.com/webhook"
                                    required
                                />
                                <small>Must be a valid HTTP or HTTPS URL</small>
                            </div>

                            <div className="form-group">
                                <label>Expected Response</label>
                                <input
                                    type="text"
                                    value={formData.expectedResponse}
                                    onChange={(e) => handleInputChange('expectedResponse', e.target.value)}
                                    placeholder="OK"
                                />
                                <small>Optional: Expected response body from webhook</small>
                            </div>

                            <div className="form-group">
                                <label>Data Format *</label>
                                <select
                                    value={formData.webhookDataFormat}
                                    onChange={(e) => handleInputChange('webhookDataFormat', e.target.value)}
                                    required
                                >
                                    <option value="signD">signD</option>
                                    <option value="JSON">JSON</option>
                                    <option value="XML">XML</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Queue Expiration (minutes) *</label>
                                <input
                                    type="number"
                                    value={formData.queueExpirationMinutes}
                                    onChange={(e) => handleInputChange('queueExpirationMinutes', parseInt(e.target.value))}
                                    min="1"
                                    max="1440"
                                    required
                                />
                                <small>Delete queued items older than this many minutes (1-1440)</small>
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.onlySubmitPositiveResults}
                                        onChange={(e) => handleInputChange('onlySubmitPositiveResults', e.target.checked)}
                                        style={{ marginRight: '8px', width: 'auto' }}
                                    />
                                    <span>Only send positive results to webhook</span>
                                </label>
                                <small>When enabled, only scan results that pass validation will be sent to this webhook</small>
                            </div>

                            <div className="form-group">
                                <label>Custom Headers</label>
                                {formData.customHeaders.map((header, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                        <input
                                            type="text"
                                            placeholder="Header Name"
                                            value={header.key}
                                            onChange={(e) => updateCustomHeader(index, 'key', e.target.value)}
                                            style={{ width: '30%' }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Header Value"
                                            value={header.value}
                                            onChange={(e) => updateCustomHeader(index, 'value', e.target.value)}
                                            style={{ width: '70%' }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeCustomHeader(index)}
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                background: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                padding: 0,
                                                flexShrink: 0
                                            }}
                                            title="Remove header"
                                        >
                                            −
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addCustomHeader}
                                    style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '8px' }}
                                >
                                    + Add Header
                                </button>
                                <small>Add custom HTTP headers to include with each webhook request (e.g., Authorization, X-API-Key)</small>
                            </div>

                            <div className="modal-footer">
                                <button type="submit" className="primary-btn">
                                    {editingWebhook ? 'Update Webhook' : 'Create Webhook'}
                                </button>
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={closeModal}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Webhooks;
